import { ReactElement, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import produce from 'immer'
import { find, findIndex } from 'lodash-es'
import { TypeOf } from 'yup'
import { Button, Stack } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { HotkeySaveButton, SaveButton } from '@ui/actions'
import { Form } from '@ui/forms'
import EditableLabel from '@ui/forms/EditableLabel'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore, useSelectionStore } from '@editorStores'
import { useResultsInvalidation } from '@editorHooks'
import { CollapsibleGeometryBoxSection } from '@editorComponents'
import { useStructuralPlanningFormEsc } from '@structuralPlanningHooks'
import { getModel } from '@queries'
import { updateSlab as updateSlabQuery } from '@mutations'
import { slabSchema } from '../../schema'
import FormFields from '../FormFields'
import SlabFormScene from '../SlabFormScene'

interface Props {
  selectedElement: string
  onClose: () => void
}

const SlabFormBase = ({ selectedElement, onClose }: Props): ReactElement => {
  const { projectId } = useParams()

  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const invalidateResults = useResultsInvalidation()

  const slabs = useModelStore(state => state.model.slabs)
  const isDrawingSlab = useSelectionStore(
    state => state.mode === 'draw-slabs' || state.mode === 'ifc-draw-slabs',
  )
  const SubmitButton = isDrawingSlab ? HotkeySaveButton : SaveButton

  const slab = useMemo(
    () => (selectedElement ? find(slabs, { guid: selectedElement }) : undefined),
    [slabs, selectedElement],
  ) as ShapeObject

  const defaultValues = useMemo(() => {
    return {
      ...slabSchema.getDefault(),
      guid: slab?.guid,
      points: slab?.shape.points.map(p => ({ x: p.x, y: p.y, z: p.z })),
    }
  }, [slab])

  useStructuralPlanningFormEsc(() => {
    onClose()
  }, !!selectedElement)

  // MUTATIONS

  const { mutateAsync, isLoading } = useMutation(
    (data: TypeOf<typeof slabSchema>) =>
      updateSlabQuery.request(projectId as string, data.guid, data.points),
    {
      onSuccess: async (data: ShapeObject) => {
        const queryData = queryClient.getQueryData<PlanarModel>(getModel.getKey(projectId))
        if (queryData) {
          const newModel = produce(queryData, draft => {
            const slabIndex = findIndex(draft.slabs, { guid: data.guid })
            if (slabIndex !== -1) {
              draft.slabs[slabIndex] = {
                ...draft.slabs[slabIndex],
                ...data,
              }
            }

            return draft
          })
          queryClient.setQueryData(getModel.getKey(projectId), newModel)
        }

        invalidateResults(projectId as string)

        queryClient.invalidateQueries(getModel.getKey(projectId))

        enqueueSnackbar('Deckenfläche erfolgreich gespeichert', { variant: 'success' })
      },
      onError: () => {
        enqueueSnackbar('Fehler beim Speichern der Deckenfläche', { variant: 'error' })
      },
    },
  )

  return (
    <>
      <Form
        key={selectedElement}
        onSubmit={data => mutateAsync(data)}
        defaultValues={defaultValues}
        validationSchema={slabSchema}
      >
        <SlabFormScene element={selectedElement} />

        <Stack direction="column" spacing={1}>
          {slab && <EditableLabel elementGuid={slab.guid} label={slab.label ?? slab.guid} />}
          <Stack direction="row" justifyContent="end" spacing={1}>
            <Button
              variant="outlined"
              onClick={onClose}
              size="small"
              fullWidth
              data-cy="btn-unselect"
            >
              abwählen
            </Button>
          </Stack>
          {selectedElement && (
            <CollapsibleGeometryBoxSection defaultExpanded={true}>
              <Stack direction="column" spacing={1}>
                <FormFields
                  selectedElement={selectedElement}
                  zComputed={false}
                  setZComputed={() => undefined}
                  showZComputedSwitch={false}
                  showCalculateZFromOtherPoints={false}
                  showPointRotation={false}
                  showTranslationAndRotation={false}
                  syncZOnChange
                  preserveZOnReplace
                />
                <Stack direction="row" justifyContent="end" spacing={1}>
                  <SubmitButton
                    type="submit"
                    loading={isLoading}
                    fullWidth
                    disabled={false}
                    data-cy="btn-slab-submit"
                  >
                    Speichern
                  </SubmitButton>
                </Stack>
              </Stack>
            </CollapsibleGeometryBoxSection>
          )}
        </Stack>
      </Form>
    </>
  )
}

export default SlabFormBase
